import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/api';
import { toast } from 'sonner';
import { 
  Building2, Upload, Save, User, MapPin, Phone, Mail, Globe, 
  FileText, CheckCircle, RefreshCw, Image, Briefcase
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to build full logo URL
const getLogoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  // Static files are served at /api/uploads/
  // Handle both old format (/uploads/...) and new format (/api/uploads/...)
  if (url.startsWith('/uploads/')) {
    return `${BACKEND_URL}/api${url}`;
  }
  return `${BACKEND_URL}${url}`;
};

export default function CompanySettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [settings, setSettings] = useState({
    // Company Identity
    company_name: '',
    trading_name: '',
    company_registration: '',
    vat_number: '',
    cidb_grade: '',
    
    // Logo
    logo_url: null,
    logo_file_name: null,
    
    // Physical Address
    physical_address_line1: '',
    physical_address_line2: '',
    physical_city: '',
    physical_province: '',
    physical_postal_code: '',
    physical_country: 'South Africa',
    
    // Postal Address
    postal_address_line1: '',
    postal_address_line2: '',
    postal_city: '',
    postal_province: '',
    postal_postal_code: '',
    postal_country: 'South Africa',
    use_physical_as_postal: true,
    
    // Contact
    telephone: '',
    fax: '',
    email: '',
    website: '',
    
    // Primary Contact
    primary_contact_name: '',
    primary_contact_surname: '',
    primary_contact_position: '',
    primary_contact_email: '',
    primary_contact_mobile: '',
    primary_contact_direct_tel: '',
    
    // Secondary Contact
    secondary_contact_name: '',
    secondary_contact_surname: '',
    secondary_contact_position: '',
    secondary_contact_email: '',
    secondary_contact_mobile: '',
    
    // Document Settings
    default_document_footer: '',
    document_reference_prefix: ''
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await api.companySettings.get();
      if (res.data) {
        setSettings(prev => ({ ...prev, ...res.data }));
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings.company_name) {
      toast.error('Company name is required');
      return;
    }
    
    setSaving(true);
    try {
      await api.companySettings.save(settings);
      toast.success('Company settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload PNG, JPG, SVG, or WebP');
      return;
    }
    
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo file must be less than 2MB');
      return;
    }
    
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.companySettings.uploadLogo(formData);
      setSettings(prev => ({
        ...prev,
        logo_url: res.data.logo_url,
        logo_file_name: res.data.file_name
      }));
      toast.success('Logo uploaded');
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-5xl space-y-6" data-testid="company-settings-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-barlow font-bold uppercase tracking-tight">Company Settings</h1>
            <p className="text-slate-500 text-sm">Configure your contractor details for documents and correspondence</p>
          </div>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="rounded-sm bg-slate-900"
            data-testid="save-settings-btn"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>

        <Tabs defaultValue="company" className="space-y-6">
          {/* Mobile: Dropdown */}
          <div className="sm:hidden mb-4">
            <Select defaultValue="company">
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select section" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="company">Company Info</SelectItem>
                <SelectItem value="address">Addresses</SelectItem>
                <SelectItem value="contacts">Contacts</SelectItem>
                <SelectItem value="documents">Documents</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Desktop: Tab List */}
          <TabsList className="bg-slate-100 p-1 rounded-sm hidden sm:inline-flex">
            <TabsTrigger value="company" className="rounded-sm data-[state=active]:bg-white">
              <Building2 className="h-4 w-4 mr-1.5" />
              Company Info
            </TabsTrigger>
            <TabsTrigger value="address" className="rounded-sm data-[state=active]:bg-white">
              <MapPin className="h-4 w-4 mr-1.5" />
              Addresses
            </TabsTrigger>
            <TabsTrigger value="contacts" className="rounded-sm data-[state=active]:bg-white">
              <User className="h-4 w-4 mr-1.5" />
              Contacts
            </TabsTrigger>
            <TabsTrigger value="documents" className="rounded-sm data-[state=active]:bg-white">
              <FileText className="h-4 w-4 mr-1.5" />
              Documents
            </TabsTrigger>
          </TabsList>

          {/* Company Info Tab */}
          <TabsContent value="company">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Logo Upload */}
              <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-5">
                <h3 className="text-sm font-barlow font-bold uppercase text-slate-700 mb-4 flex items-center gap-2">
                  <Image className="h-4 w-4" />
                  Company Logo
                </h3>
                <div className="text-center">
                  {settings.logo_url ? (
                    <div className="mb-4">
                      <img 
                        src={getLogoUrl(settings.logo_url)} 
                        alt="Company Logo" 
                        className="max-h-32 max-w-full mx-auto border border-slate-200 rounded-sm p-2"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <p className="text-xs text-slate-500 mt-2">{settings.logo_file_name}</p>
                    </div>
                  ) : (
                    <div className="mb-4 p-8 border-2 border-dashed border-slate-200 rounded-sm">
                      <Image className="h-12 w-12 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-400">No logo uploaded</p>
                    </div>
                  )}
                  <input
                    type="file"
                    accept=".png,.jpg,.jpeg,.svg,.webp"
                    onChange={handleLogoUpload}
                    className="hidden"
                    id="logo-upload"
                  />
                  <label htmlFor="logo-upload">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="rounded-sm cursor-pointer"
                      disabled={uploadingLogo}
                      asChild
                    >
                      <span>
                        <Upload className="h-4 w-4 mr-1" />
                        {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                      </span>
                    </Button>
                  </label>
                  <p className="text-xs text-slate-400 mt-2">PNG, JPG, SVG or WebP. Max 2MB.</p>
                </div>
              </div>

              {/* Company Details */}
              <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm shadow-sm p-5">
                <h3 className="text-sm font-barlow font-bold uppercase text-slate-700 mb-4 flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Company Details
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label className="text-xs font-bold uppercase text-slate-500">Company Name *</Label>
                    <Input
                      value={settings.company_name}
                      onChange={(e) => updateField('company_name', e.target.value)}
                      placeholder="ABC Construction (Pty) Ltd"
                      className="rounded-sm mt-1"
                      data-testid="company-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase text-slate-500">Trading As</Label>
                    <Input
                      value={settings.trading_name}
                      onChange={(e) => updateField('trading_name', e.target.value)}
                      placeholder="ABC Builders"
                      className="rounded-sm mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase text-slate-500">Registration Number</Label>
                    <Input
                      value={settings.company_registration}
                      onChange={(e) => updateField('company_registration', e.target.value)}
                      placeholder="2020/123456/07"
                      className="rounded-sm mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase text-slate-500">VAT Number</Label>
                    <Input
                      value={settings.vat_number}
                      onChange={(e) => updateField('vat_number', e.target.value)}
                      placeholder="4123456789"
                      className="rounded-sm mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase text-slate-500">CIDB Grade</Label>
                    <Input
                      value={settings.cidb_grade}
                      onChange={(e) => updateField('cidb_grade', e.target.value)}
                      placeholder="9CE PE"
                      className="rounded-sm mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Addresses Tab */}
          <TabsContent value="address">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Physical Address */}
              <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-5">
                <h3 className="text-sm font-barlow font-bold uppercase text-slate-700 mb-4">
                  Physical Address
                </h3>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs font-bold uppercase text-slate-500">Address Line 1</Label>
                    <Input
                      value={settings.physical_address_line1}
                      onChange={(e) => updateField('physical_address_line1', e.target.value)}
                      placeholder="123 Main Street"
                      className="rounded-sm mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase text-slate-500">Address Line 2</Label>
                    <Input
                      value={settings.physical_address_line2}
                      onChange={(e) => updateField('physical_address_line2', e.target.value)}
                      placeholder="Suite 100, Building A"
                      className="rounded-sm mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500">City</Label>
                      <Input
                        value={settings.physical_city}
                        onChange={(e) => updateField('physical_city', e.target.value)}
                        placeholder="Johannesburg"
                        className="rounded-sm mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500">Province</Label>
                      <Input
                        value={settings.physical_province}
                        onChange={(e) => updateField('physical_province', e.target.value)}
                        placeholder="Gauteng"
                        className="rounded-sm mt-1"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500">Postal Code</Label>
                      <Input
                        value={settings.physical_postal_code}
                        onChange={(e) => updateField('physical_postal_code', e.target.value)}
                        placeholder="2000"
                        className="rounded-sm mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500">Country</Label>
                      <Input
                        value={settings.physical_country}
                        onChange={(e) => updateField('physical_country', e.target.value)}
                        className="rounded-sm mt-1"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Details */}
              <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-5">
                <h3 className="text-sm font-barlow font-bold uppercase text-slate-700 mb-4">
                  Contact Details
                </h3>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs font-bold uppercase text-slate-500">Telephone</Label>
                    <Input
                      value={settings.telephone}
                      onChange={(e) => updateField('telephone', e.target.value)}
                      placeholder="+27 11 123 4567"
                      className="rounded-sm mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase text-slate-500">Fax</Label>
                    <Input
                      value={settings.fax}
                      onChange={(e) => updateField('fax', e.target.value)}
                      placeholder="+27 11 123 4568"
                      className="rounded-sm mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase text-slate-500">Email</Label>
                    <Input
                      type="email"
                      value={settings.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      placeholder="info@abcconstruction.co.za"
                      className="rounded-sm mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase text-slate-500">Website</Label>
                    <Input
                      value={settings.website}
                      onChange={(e) => updateField('website', e.target.value)}
                      placeholder="www.abcconstruction.co.za"
                      className="rounded-sm mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Primary Contact */}
              <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-5">
                <h3 className="text-sm font-barlow font-bold uppercase text-slate-700 mb-4 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Primary Contact (Document Signatory)
                </h3>
                <p className="text-xs text-slate-500 mb-4">This person will appear as the signatory on formal documents</p>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500">First Name</Label>
                      <Input
                        value={settings.primary_contact_name}
                        onChange={(e) => updateField('primary_contact_name', e.target.value)}
                        placeholder="John"
                        className="rounded-sm mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500">Surname</Label>
                      <Input
                        value={settings.primary_contact_surname}
                        onChange={(e) => updateField('primary_contact_surname', e.target.value)}
                        placeholder="Smith"
                        className="rounded-sm mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase text-slate-500">Position / Title</Label>
                    <Input
                      value={settings.primary_contact_position}
                      onChange={(e) => updateField('primary_contact_position', e.target.value)}
                      placeholder="Contracts Director"
                      className="rounded-sm mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase text-slate-500">Email</Label>
                    <Input
                      type="email"
                      value={settings.primary_contact_email}
                      onChange={(e) => updateField('primary_contact_email', e.target.value)}
                      placeholder="john.smith@abcconstruction.co.za"
                      className="rounded-sm mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500">Mobile</Label>
                      <Input
                        value={settings.primary_contact_mobile}
                        onChange={(e) => updateField('primary_contact_mobile', e.target.value)}
                        placeholder="+27 82 123 4567"
                        className="rounded-sm mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500">Direct Tel</Label>
                      <Input
                        value={settings.primary_contact_direct_tel}
                        onChange={(e) => updateField('primary_contact_direct_tel', e.target.value)}
                        placeholder="+27 11 123 4569"
                        className="rounded-sm mt-1"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Secondary Contact */}
              <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-5">
                <h3 className="text-sm font-barlow font-bold uppercase text-slate-700 mb-4 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Secondary Contact
                </h3>
                <p className="text-xs text-slate-500 mb-4">Alternative contact person for correspondence</p>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500">First Name</Label>
                      <Input
                        value={settings.secondary_contact_name}
                        onChange={(e) => updateField('secondary_contact_name', e.target.value)}
                        placeholder="Jane"
                        className="rounded-sm mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500">Surname</Label>
                      <Input
                        value={settings.secondary_contact_surname}
                        onChange={(e) => updateField('secondary_contact_surname', e.target.value)}
                        placeholder="Doe"
                        className="rounded-sm mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase text-slate-500">Position / Title</Label>
                    <Input
                      value={settings.secondary_contact_position}
                      onChange={(e) => updateField('secondary_contact_position', e.target.value)}
                      placeholder="Contracts Manager"
                      className="rounded-sm mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase text-slate-500">Email</Label>
                    <Input
                      type="email"
                      value={settings.secondary_contact_email}
                      onChange={(e) => updateField('secondary_contact_email', e.target.value)}
                      placeholder="jane.doe@abcconstruction.co.za"
                      className="rounded-sm mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase text-slate-500">Mobile</Label>
                    <Input
                      value={settings.secondary_contact_mobile}
                      onChange={(e) => updateField('secondary_contact_mobile', e.target.value)}
                      placeholder="+27 82 987 6543"
                      className="rounded-sm mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-5">
              <h3 className="text-sm font-barlow font-bold uppercase text-slate-700 mb-4 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Document Settings
              </h3>
              <div className="space-y-4 max-w-2xl">
                <div>
                  <Label className="text-xs font-bold uppercase text-slate-500">Document Reference Prefix</Label>
                  <Input
                    value={settings.document_reference_prefix}
                    onChange={(e) => updateField('document_reference_prefix', e.target.value)}
                    placeholder="ABC-"
                    className="rounded-sm mt-1 max-w-xs"
                  />
                  <p className="text-xs text-slate-400 mt-1">Prefix for document reference numbers (e.g., ABC-001, ABC-002)</p>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase text-slate-500">Default Document Footer</Label>
                  <Textarea
                    value={settings.default_document_footer}
                    onChange={(e) => updateField('default_document_footer', e.target.value)}
                    placeholder="ABC Construction (Pty) Ltd | Reg: 2020/123456/07 | VAT: 4123456789"
                    className="rounded-sm mt-1"
                    rows={2}
                  />
                  <p className="text-xs text-slate-400 mt-1">This text will appear at the bottom of printed documents</p>
                </div>
              </div>
            </div>

            {/* Preview */}
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-5 mt-6">
              <h3 className="text-sm font-barlow font-bold uppercase text-slate-700 mb-4">Document Header Preview</h3>
              <div className="bg-white border border-slate-200 rounded-sm p-6">
                <div className="flex items-start justify-between border-b border-slate-200 pb-4 mb-4">
                  <div className="flex items-center gap-4">
                    {settings.logo_url ? (
                      <img src={getLogoUrl(settings.logo_url)} alt="Logo" className="h-16 w-auto" onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div className="h-16 w-16 bg-slate-100 rounded-sm flex items-center justify-center">
                        <Image className="h-8 w-8 text-slate-300" />
                      </div>
                    )}
                    <div>
                      <p className="font-bold text-lg">{settings.company_name || '[Company Name]'}</p>
                      {settings.trading_name && (
                        <p className="text-sm text-slate-600">t/a {settings.trading_name}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right text-sm text-slate-600">
                    <p>{settings.physical_address_line1 || '[Address Line 1]'}</p>
                    {settings.physical_address_line2 && <p>{settings.physical_address_line2}</p>}
                    <p>{settings.physical_city || '[City]'}, {settings.physical_province || '[Province]'} {settings.physical_postal_code}</p>
                    <p className="mt-2">{settings.telephone || '[Telephone]'}</p>
                    <p>{settings.email || '[Email]'}</p>
                  </div>
                </div>
                <div className="text-center py-8 text-slate-400 border-b border-slate-200 mb-4">
                  [Document Content]
                </div>
                <div className="text-sm">
                  <p className="font-medium">{settings.primary_contact_name || '[Name]'} {settings.primary_contact_surname}</p>
                  <p className="text-slate-600">{settings.primary_contact_position || '[Position]'}</p>
                </div>
                {settings.default_document_footer && (
                  <div className="mt-6 pt-4 border-t border-slate-200 text-xs text-center text-slate-500">
                    {settings.default_document_footer}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
